import { z } from 'zod';

// Sites
export const venueSchema = z.object({
  venueId: z.string(),
  name: z.string(),
  type: z.enum(['restaurant', 'bar', 'buffet', 'lounge', 'room-service', 'catering']),
  isActive: z.boolean(),
});

export const storageLocationSchema = z.object({
  locationId: z.string(),
  name: z.string(),
  type: z.enum(['freezer', 'cooler', 'dry-storage', 'bar-storage', 'pantry']),
  isActive: z.boolean(),
});

export const siteSchema = z.object({
  siteId: z.string(),
  name: z.string(),
  code: z.string(),
  type: z.enum(['ship', 'hotel', 'restaurant', 'kitchen', 'warehouse']),
  status: z.enum(['active', 'inactive', 'maintenance']),
  timezone: z.string(),
  currency: z.string(),
  defaultLanguage: z.string(),
  supportedLanguages: z.array(z.string()),
  venues: z.array(venueSchema),
  storageLocations: z.array(storageLocationSchema),
  address: z.string().optional(),
  contactEmail: z.string().optional(),
  contactPhone: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Venue = z.infer<typeof venueSchema>;
export type StorageLocation = z.infer<typeof storageLocationSchema>;
export type Site = z.infer<typeof siteSchema>;
export type SiteStatus = Site['status'];
export type SiteType = Site['type'];

export const sitesResponseSchema = z.object({
  data: z.array(siteSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
});

export type SitesResponse = z.infer<typeof sitesResponseSchema>;

export const SITE_STATUS_COLORS: Record<SiteStatus, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  maintenance: 'bg-amber-100 text-amber-800',
};

export const SITE_TYPE_LABELS: Record<SiteType, string> = {
  ship: 'Ship',
  hotel: 'Hotel',
  restaurant: 'Restaurant',
  kitchen: 'Kitchen',
  warehouse: 'Warehouse',
};

// Languages
export const languageSchema = z.object({
  code: z.string(),
  name: z.string(),
  nativeName: z.string(),
  isEnabled: z.boolean(),
  isDefault: z.boolean(),
  translationProgress: z.number(),
  lastUpdated: z.string(),
});

export type Language = z.infer<typeof languageSchema>;

export const languagesResponseSchema = z.object({
  data: z.array(languageSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
});

export type LanguagesResponse = z.infer<typeof languagesResponseSchema>;
